import { useEffect, useState } from 'react';
import { adminAPI, fmtError } from '@/lib/api';
import { Plus, X, Zap, Trash2, Users } from 'lucide-react';

const APPLIES_TO_OPTS = [
  { value: 'one_step_entry', label: 'One-Step entry fee (live)' },
  { value: 'instant_access', label: 'Instant Funding access fee (live)' },
  { value: 'evaluation_fee', label: 'Evaluation fee — legacy plans (retired)' },
  { value: 'split_unlock', label: 'Split Fee unlock fee — legacy (retired)' },
];

const empty = {
  code: '',
  name: '',
  percent: 25,
  applies_to: 'one_step_entry',
  duration_hours: 48,
  promo_tag: '',
  banner_label: '',
  active: true,
};

const emptyAffiliate = {
  code: '',
  percent: 20,
  affiliate_email: '',
};

export default function AdminPromotions() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [creatingAffiliate, setCreatingAffiliate] = useState(false);
  const [form, setForm] = useState(empty);
  const [affForm, setAffForm] = useState(emptyAffiliate);

  const load = async () => {
    try { setRows(await adminAPI.promotions()); setErr(''); }
    catch (e) { setErr(fmtError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setErr('');
    try {
      await adminAPI.createPromo({ ...form, code: form.code.trim().toUpperCase() });
      setForm(empty); setCreating(false); load();
    } catch (e) { setErr(fmtError(e)); }
  };

  const submitAffiliate = async () => {
    setErr('');
    try {
      await adminAPI.createAffiliateCode({
        ...affForm,
        code: affForm.code.trim().toUpperCase(),
        affiliate_email: affForm.affiliate_email.trim(),
      });
      setAffForm(emptyAffiliate); setCreatingAffiliate(false); load();
    } catch (e) { setErr(fmtError(e)); }
  };

  const endNow = async (id) => {
    if (!window.confirm('End this promo now? It will stop applying immediately.')) return;
    try { await adminAPI.endPromoNow(id); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this promo? Past orders that referenced it are unaffected.')) return;
    try { await adminAPI.deletePromo(id); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  const toggleActive = async (p) => {
    try { await adminAPI.updatePromo(p.promo_id, { active: !p.active }); load(); }
    catch (e) { setErr(fmtError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl">Promotions</h1>
        <div className="flex gap-2">
          <button className="btn-ghost btn-sm" onClick={() => setCreatingAffiliate(true)} data-testid="new-affiliate-code-btn">
            <Users size={14} /> New affiliate code
          </button>
          <button className="btn-primary btn-sm" onClick={() => setCreating(true)} data-testid="new-promo-btn">
            <Plus size={14} /> New sale promo
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-[#ff3366]" data-testid="promo-err">{err}</div>}

      <div className="card-tech rounded-lg overflow-x-auto">
        <div className="min-w-[1260px]">
          <div className="grid grid-cols-[0.9fr_1.5fr_0.6fr_1.2fr_1.3fr_1.2fr_0.8fr_0.8fr_1.1fr] gap-3 px-4 py-3 label-xs border-b border-white/5">
            <div>CODE</div><div>NAME</div><div>%</div><div>APPLIES TO</div><div>AFFILIATE</div><div>EXPIRES</div><div>STATUS</div><div>ACTIVE</div><div>ACTIONS</div>
          </div>
          {rows.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">No promotions yet — create one above.</div>}
          {rows.map(p => (
            <div key={p.promo_id} className="grid grid-cols-[0.9fr_1.5fr_0.6fr_1.2fr_1.3fr_1.2fr_0.8fr_0.8fr_1.1fr] gap-3 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`promo-row-${p.promo_id}`}>
              <div className="font-mono text-white">{p.code}</div>
              <div className="text-zinc-300 truncate" title={p.name}>{p.name}</div>
              <div className="font-mono text-[#00ff66]">{p.percent}%</div>
              <div className="text-zinc-400 text-xs">{p.applies_to}</div>
              <div className="text-zinc-400 text-xs truncate" title={p.affiliate_email || ''}>
                {p.affiliate_email || <span className="text-zinc-600">—</span>}
              </div>
              <div className="text-xs font-mono text-zinc-400" title={p.expires_at}>
                {p.expires_at && p.expires_at.startsWith('2099') ? 'No expiry' : new Date(p.expires_at).toLocaleString()}
              </div>
              <div>
                {p.is_live
                  ? <span className="chip chip-green">LIVE</span>
                  : <span className="chip chip-yellow">EXPIRED</span>}
              </div>
              <div>
                <button
                  onClick={() => toggleActive(p)}
                  className={`px-2 py-1 rounded text-xs font-mono border ${p.active ? 'border-[#00ff66]/40 text-[#00ff66]' : 'border-white/10 text-zinc-500'}`}
                  data-testid={`toggle-active-${p.promo_id}`}
                >
                  {p.active ? 'ON' : 'OFF'}
                </button>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {p.is_live && (
                  <button className="btn-ghost btn-sm text-[#ff8c42]" onClick={() => endNow(p.promo_id)} data-testid={`end-now-${p.promo_id}`}>
                    <Zap size={12} /> End now
                  </button>
                )}
                <button className="btn-ghost btn-sm text-[#ff3366]" onClick={() => remove(p.promo_id)} data-testid={`del-${p.promo_id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 text-[11px] text-zinc-500 border-t border-white/5">
          Affiliate codes apply to both One-Step and Instant Funding, so each one shows as two rows here (same code, same affiliate, two "applies to" buckets) — that's expected, not a duplicate.
        </div>
      </div>

      {/* ---- New sale promo (time-boxed, single bucket, storefront-advertised) ---- */}
      {creating && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="card-tech rounded-lg p-6 max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl">New sale promo</h3>
              <button onClick={() => setCreating(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">A storefront-wide or checkout-advertised sale on one product. For a partner's personal code, use "New affiliate code" instead.</p>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-xs mb-1">Code</div>
                  <input className="input-tech" placeholder="HALLOWEEN" value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="promo-code" />
                </div>
                <div>
                  <div className="label-xs mb-1">Discount %</div>
                  <input className="input-tech" type="number" min={0} max={100} step={1} value={form.percent}
                    onChange={e => setForm({ ...form, percent: Number(e.target.value) })} data-testid="promo-percent" />
                </div>
              </div>
              <div>
                <div className="label-xs mb-1">Display name</div>
                <input className="input-tech" placeholder="Halloween Sale" value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })} data-testid="promo-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-xs mb-1">Applies to</div>
                  <select className="input-tech" value={form.applies_to}
                    onChange={e => setForm({ ...form, applies_to: e.target.value })} data-testid="promo-applies">
                    {APPLIES_TO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="label-xs mb-1">Duration (hours)</div>
                  <input className="input-tech" type="number" min={1} step={1} value={form.duration_hours}
                    onChange={e => setForm({ ...form, duration_hours: Number(e.target.value) })} data-testid="promo-duration" />
                </div>
              </div>
              <div>
                <div className="label-xs mb-1">Promo tag (shown on product cards)</div>
                <input className="input-tech" placeholder="HALLOWEEN · 30% OFF" value={form.promo_tag}
                  onChange={e => setForm({ ...form, promo_tag: e.target.value })} data-testid="promo-tag" />
              </div>
              <div>
                <div className="label-xs mb-1">Banner label (top of every page)</div>
                <input className="input-tech" placeholder="Halloween Sale Ends In" value={form.banner_label}
                  onChange={e => setForm({ ...form, banner_label: e.target.value })} data-testid="promo-banner-label" />
              </div>
              {err && <div className="text-sm text-[#ff3366]">{err}</div>}
              <div className="flex gap-2">
                <button className="btn-primary" onClick={submit} disabled={!form.code || !form.name} data-testid="submit-promo">Create promo</button>
                <button className="btn-ghost" onClick={() => setCreating(false)}>Cancel</button>
              </div>
              <div className="text-xs text-zinc-500">
                Promos are time-bounded. The countdown banner updates automatically; once `expires_at` passes the promo is rejected by the API and the banner hides itself — no manual cleanup needed.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- New affiliate code (standing, applies everywhere, never advertised) ---- */}
      {creatingAffiliate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCreatingAffiliate(false)}>
          <div className="card-tech rounded-lg p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl">New affiliate code</h3>
              <button onClick={() => setCreatingAffiliate(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              A partner's own code. Applies to every program automatically, never expires, and is never shown on the storefront or checkout — only works if the affiliate shares it themselves.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <div className="label-xs mb-1">Code</div>
                <input className="input-tech" placeholder="OFU20" value={affForm.code}
                  onChange={e => setAffForm({ ...affForm, code: e.target.value.toUpperCase() })} data-testid="aff-code" />
              </div>
              <div>
                <div className="label-xs mb-1">Discount %</div>
                <input className="input-tech" type="number" min={0} max={100} step={1} value={affForm.percent}
                  onChange={e => setAffForm({ ...affForm, percent: Number(e.target.value) })} data-testid="aff-percent" />
              </div>
              <div>
                <div className="label-xs mb-1">Affiliate's account email</div>
                <input className="input-tech" type="email" placeholder="partner@example.com" value={affForm.affiliate_email}
                  onChange={e => setAffForm({ ...affForm, affiliate_email: e.target.value })} data-testid="aff-email" />
                <div className="text-[11px] text-zinc-500 mt-1">
                  They must already have a registered account — every order paid with this code credits their affiliate balance automatically.
                </div>
              </div>
              {err && <div className="text-sm text-[#ff3366]">{err}</div>}
              <div className="flex gap-2">
                <button className="btn-primary" onClick={submitAffiliate}
                  disabled={!affForm.code || !affForm.affiliate_email} data-testid="submit-affiliate-code">
                  Create affiliate code
                </button>
                <button className="btn-ghost" onClick={() => setCreatingAffiliate(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}